import { ChangeDetectorRef, Component, effect, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzLayoutModule, NzSiderComponent } from 'ng-zorro-antd/layout';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { AppShellService } from '../../core/services/app-shell.service';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';
import { LayoutPreferenceService } from '../../core/services/layout-preference.service';
import { getExpandedGroupLabels, isMainMenuItem } from '../../core/utils/navigation.util';
import { MenuItem } from '../../routes';

@Component({
  selector: 'veitros-legacy-shell',
  standalone: true,
  imports: [
    CommonModule, RouterOutlet, RouterLink, RouterLinkActive,
    NzIconModule, NzMenuModule, NzLayoutModule, NzDrawerModule,
    NzGridModule, NzSkeletonModule, NzButtonModule,
    NzToolTipModule, NzTagModule,
  ],
  templateUrl: './legacy-shell.component.html',
  styleUrl: './legacy-shell.component.scss',
})
export class LegacyShellComponent implements OnInit, OnDestroy {
  shell = inject(AppShellService);
  private featureFlags = inject(FeatureFlagsService);
  private layoutPref = inject(LayoutPreferenceService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  @ViewChild('sider') sider: NzSiderComponent;

  openKeys: string[] = [];
  private routerSub: Subscription | null = null;

  private readonly layoutStateFx = effect(() => {
    this.shell.isMobile();
    this.shell.isCollapsed();
    this.cdr.markForCheck();
  });

  ngOnInit(): void {
    void this.featureFlags.load().then(() => this.cdr.markForCheck());

    this.syncMenuOpenKeys(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = (event as NavigationEnd).urlAfterRedirects ?? (event as NavigationEnd).url;
        this.syncMenuOpenKeys(url);
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  get visibleLegacyRoutes(): MenuItem[] {
    return this.shell.legacyRoutes.filter(isMainMenuItem);
  }

  get showTryNewLayout(): boolean {
    return this.featureFlags.isEnabled('ui.layout_v2');
  }

  tryNewLayout(): void {
    this.layoutPref.enable();
    window.location.reload();
  }

  onSubmenuOpenChange(label: string, open: boolean): void {
    if (open) {
      if (!this.openKeys.includes(label)) {
        this.openKeys = [...this.openKeys, label];
      }
      return;
    }

    this.openKeys = this.openKeys.filter((key) => key !== label);
  }

  isSubmenuOpen(label: string): boolean {
    return this.openKeys.includes(label);
  }

  onSiderCollapseChange(collapsed: boolean): void {
    if (collapsed) {
      this.openKeys = [];
    }
    if (this.shell.isCollapsed() !== collapsed) {
      this.shell.setSiderCollapsed(collapsed);
    }
  }

  private syncMenuOpenKeys(url: string): void {
    const expanded = getExpandedGroupLabels(url, this.visibleLegacyRoutes);
    this.openKeys = [...expanded];
  }
}
